import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { api, ApiError } from '../../lib/api'
import type { SessionUser } from '@toga/shared'

const roleLabel: Record<string, string> = {
  ADMIN: 'Administrador',
  ABOGADO: 'Abogado',
  PARALEGAL: 'Paralegal',
  LECTURA: 'Solo lectura',
}

export default function Profile() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const [fullName, setFullName] = useState(user?.fullName ?? '')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (user) setFullName(user.fullName)
  }, [user])

  if (!user) return null

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      await api.patch<{ user: SessionUser }>('/auth/me', { fullName })
      await auth.refresh()
      setMessage({ kind: 'ok', text: 'Perfil actualizado.' })
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : 'Error de red'
      setMessage({ kind: 'error', text })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Mi perfil</h2>
        <p className="t-muted text-sm mt-1">Tu información personal y de acceso.</p>
      </header>

      <div className="surface p-6">
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm t-secondary mb-1.5">Email</label>
            <input
              value={user.email}
              disabled
              className="w-full px-3.5 py-2 rounded-lg bg-slate-900/40 border border-slate-700/40 text-slate-400 cursor-not-allowed"
            />
            <p className="text-xs t-faint mt-1">
              Para cambiar el email, un administrador debe crearte un nuevo usuario.
            </p>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">Rol</label>
            <input
              value={roleLabel[user.role] ?? user.role}
              disabled
              className="w-full px-3.5 py-2 rounded-lg bg-slate-900/40 border border-slate-700/40 text-slate-400 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">Nombre completo</label>
            <input
              required
              minLength={2}
              maxLength={200}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3.5 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>

          {message && (
            <div
              className={[
                'text-sm rounded-lg px-3 py-2 border',
                message.kind === 'ok'
                  ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                  : 'text-rose-300 bg-rose-500/10 border-rose-500/30',
              ].join(' ')}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={saving || fullName === user.fullName}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Guardar cambios
          </button>
        </form>
      </div>
    </div>
  )
}
